import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";
import {
  Box,
  Button,
  Typography,
  Stack,
  Chip,
  Paper,
  Tooltip,
} from "@mui/material";
import {
  createTheme,
  ThemeProvider,
  styled,
  keyframes,
  alpha,
} from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import PublicIcon from "@mui/icons-material/Public";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ImageIcon from "@mui/icons-material/Image";

// ─── Theme ────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#2563EB", light: "#DBEAFE", dark: "#1D4ED8" },
    secondary: { main: "#C4922A", light: "#FEF3C7" },
    background: { default: "#F0F2F8", paper: "#FFFFFF" },
    text: { primary: "#0F1117", secondary: "#4B5563", disabled: "#9CA3AF" },
  },
  typography: {
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    h2: { fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
});

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const cardEntrance = keyframes`
  from { opacity: 0; transform: translateY(14px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;
const skeletonPulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
`;

// ─── Styled Components ────────────────────────────────────────────────────────
const PageWrapper = styled(Box)(() => ({
  minHeight: "100vh",
  background: "linear-gradient(145deg, #EEF2FF 0%, #F0F2F8 40%, #E8EDF8 100%)",
  padding: "28px 20px 60px",
  fontFamily: "'Sora', sans-serif",
  "&::before": {
    content: '""',
    position: "fixed",
    top: -120,
    right: -120,
    width: 480,
    height: 480,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(37,99,235,0.06) 0%, transparent 70%)",
    pointerEvents: "none",
  },
}));

const ContentBox = styled(Box)({ maxWidth: 860, margin: "0 auto" });

const SectionCard = styled(Paper, {
  shouldForwardProp: (p) => p !== "delay",
})(({ theme, delay }) => ({
  borderRadius: 16,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 8px 24px rgba(0,0,0,0.04)",
  padding: "24px 28px",
  marginBottom: 20,
  animation: `${fadeUp} 0.5s ease both`,
  animationDelay: `${delay ?? 0}ms`,
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(37,99,235,0.06), 0 12px 32px rgba(0,0,0,0.06)",
  },
}));

const CardLabel = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.9px",
  color: theme.palette.text.disabled,
  marginBottom: 18,
  display: "flex",
  alignItems: "center",
  gap: 8,
  "&::before": {
    content: '""',
    display: "inline-block",
    width: 3,
    height: 14,
    background: theme.palette.primary.main,
    borderRadius: 2,
  },
}));

const BackBtn = styled(Button)(({ theme }) => ({
  borderRadius: 40,
  padding: "7px 18px 7px 12px",
  fontSize: 13,
  fontWeight: 600,
  textTransform: "none",
  color: theme.palette.text.secondary,
  border: `1px solid ${alpha("#000", 0.12)}`,
  background: "#fff",
  transition: "all 0.2s ease",
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.05),
    borderColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    transform: "translateX(-2px)",
  },
}));

const PrimaryBtn = styled(Button)(() => ({
  background: "linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)",
  color: "#fff",
  borderRadius: 10,
  padding: "11px 28px",
  fontSize: 14,
  fontWeight: 700,
  textTransform: "none",
  fontFamily: "'Sora', sans-serif",
  boxShadow: "0 4px 14px rgba(37,99,235,0.28)",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #1D4ED8 0%, #1E3A8A 100%)",
    boxShadow: "0 6px 20px rgba(37,99,235,0.38)",
    transform: "translateY(-1px)",
  },
  "&:active": { transform: "translateY(0)" },
  "&.Mui-disabled": {
    background: "#E5E7EB",
    color: "#9CA3AF",
    boxShadow: "none",
  },
}));

const StyledInput = styled("input")(() => ({
  width: "100%",
  fontFamily: "'Sora', sans-serif",
  fontSize: 14,
  color: "#0F1117",
  background: alpha("#F0F2F8", 0.8),
  border: `1px solid ${alpha("#000", 0.1)}`,
  borderRadius: 10,
  padding: "11px 14px",
  outline: "none",
  boxSizing: "border-box",
  transition: "all 0.2s ease",
  "&:focus": {
    borderColor: "#2563EB",
    background: "#fff",
    boxShadow: "0 0 0 3px rgba(37,99,235,0.08)",
  },
  "&::placeholder": { color: "#9CA3AF" },
}));

const FieldLabel = styled(Typography)(() => ({
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.6px",
  textTransform: "uppercase",
  color: "#9CA3AF",
  marginBottom: 6,
  fontFamily: "'Sora', sans-serif",
}));

const FlagUploadZone = styled(Box, {
  shouldForwardProp: (p) => p !== "hasfile" && p !== "dragover",
})(({ theme, hasfile, dragover }) => ({
  border: `2px dashed ${
    hasfile ? "#059669" : dragover ? "#2563EB" : alpha("#2563EB", 0.22)
  }`,
  borderRadius: 12,
  padding: "18px 16px",
  textAlign: "center",
  cursor: "pointer",
  background: hasfile
    ? alpha("#059669", 0.03)
    : dragover
    ? alpha("#2563EB", 0.04)
    : alpha("#F0F2F8", 0.7),
  transition: "all 0.25s ease",
  "&:hover": {
    borderColor: hasfile ? "#059669" : "#2563EB",
    background: hasfile ? alpha("#059669", 0.05) : alpha("#2563EB", 0.03),
    "& .flag-upload-icon": { transform: "translateY(-2px) scale(1.05)" },
  },
}));

const CountryRow = styled(Paper, {
  shouldForwardProp: (p) => p !== "itemdelay" && p !== "isediting",
})(({ theme, itemdelay, isediting }) => ({
  borderRadius: 13,
  border: `1.5px solid ${
    isediting ? alpha("#2563EB", 0.35) : alpha("#000", 0.07)
  }`,
  boxShadow: isediting
    ? `0 0 0 3px ${alpha("#2563EB", 0.08)}, 0 4px 14px rgba(37,99,235,0.1)`
    : "0 1px 4px rgba(0,0,0,0.04), 0 4px 14px rgba(0,0,0,0.03)",
  padding: "14px 18px",
  display: "flex",
  alignItems: "center",
  gap: 14,
  marginBottom: 10,
  animation: `${cardEntrance} 0.4s ease both`,
  animationDelay: `${itemdelay ?? 0}ms`,
  transition: "all 0.22s ease",
  background: isediting ? alpha("#2563EB", 0.02) : "#fff",
  "&:last-child": { marginBottom: 0 },
  "&:hover": {
    boxShadow: "0 4px 16px rgba(37,99,235,0.08), 0 8px 24px rgba(0,0,0,0.05)",
    transform: "translateY(-1px)",
    borderColor: isediting ? alpha("#2563EB", 0.4) : alpha("#2563EB", 0.15),
  },
}));

const FlagAvatar = styled(Box)(() => ({
  width: 44,
  height: 44,
  borderRadius: 11,
  flexShrink: 0,
  overflow: "hidden",
  border: `1px solid ${alpha("#000", 0.08)}`,
  background: alpha("#2563EB", 0.06),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
}));

const EditBtn = styled(Button)(({ theme }) => ({
  minWidth: "unset",
  padding: "6px 14px",
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 700,
  textTransform: "none",
  background: alpha("#C4922A", 0.1),
  color: "#C4922A",
  fontFamily: "'Sora', sans-serif",
  transition: "all 0.18s ease",
  "&:hover": {
    background: "#C4922A",
    color: "#fff",
    transform: "translateY(-1px)",
    boxShadow: `0 4px 10px ${alpha("#C4922A", 0.3)}`,
  },
}));

const SkeletonRow = styled(Box)(() => ({
  borderRadius: 13,
  border: `1px solid ${alpha("#000", 0.06)}`,
  padding: "14px 18px",
  display: "flex",
  alignItems: "center",
  gap: 14,
  background: "#fff",
  marginBottom: 10,
  animation: `${skeletonPulse} 1.5s ease-in-out infinite`,
}));

const SkeletonBlock = ({ w = "100%", h = 14, radius = 6 }) => (
  <Box
    sx={{
      width: w,
      height: h,
      borderRadius: radius,
      background: alpha("#000", 0.07),
      flexShrink: 0,
    }}
  />
);

// ─── Main Component ───────────────────────────────────────────────────────────
const AdminCountryPage = () => {
  const [countries, setCountries] = useState([]);
  const [name, setName] = useState("");
  const [currency, setCurrency] = useState("");
  const [flag, setFlag] = useState(null);
  const [flagPreview, setFlagPreview] = useState(null);
  const [editingCountry, setEditingCountry] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    setFetching(true);
    try {
      const res = await axios.get(`${API_URL}/admin/countries/`);
      setCountries(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setFetching(false);
    }
  };

  const buildFormData = () => {
    const formData = new FormData();
    formData.append("name", name);
    formData.append("currency", currency);
    if (flag) formData.append("flag", flag);
    return formData;
  };

  const handleCreateCountry = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/admin/countries/`, buildFormData());
      fetchCountries();
      resetForm();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCountry = async () => {
    setLoading(true);
    try {
      await axios.put(
        `${API_URL}/admin/countries/${editingCountry.id}/`,
        buildFormData()
      );
      fetchCountries();
      resetForm();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName("");
    setCurrency("");
    setFlag(null);
    setFlagPreview(null);
    setEditingCountry(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFlag(file);
      setFlagPreview(URL.createObjectURL(file));
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      setFlag(file);
      setFlagPreview(URL.createObjectURL(file));
    }
  };

  const startEdit = (country) => {
    setName(country.name);
    setCurrency(country.currency);
    setEditingCountry(country);
    setFlag(null);
    setFlagPreview(country.flag || null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const displayPreview = flagPreview;

  return (
    <ThemeProvider theme={theme}>
      <link
        href="https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Serif+Display&display=swap"
        rel="stylesheet"
      />

      <PageWrapper>
        <ContentBox>
          {/* ── Header ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 4,
              animation: `${fadeUp} 0.4s ease both`,
              flexWrap: "wrap",
            }}
          >
            <BackBtn
              startIcon={<ArrowBackIcon sx={{ fontSize: "15px !important" }} />}
              onClick={() => navigate(-1)}
            >
              Back
            </BackBtn>
            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: 24, sm: 30 },
                  color: "text.primary",
                  lineHeight: 1.1,
                }}
              >
                {editingCountry ? "Edit Country" : "Countries"}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "text.disabled",
                  mt: 0.3,
                  fontFamily: "'Sora', sans-serif",
                }}
              >
                Manage countries and their currencies
              </Typography>
            </Box>
            <Chip
              label={editingCountry ? "Editing" : "New"}
              size="small"
              sx={{
                ml: "auto",
                fontFamily: "'Sora', sans-serif",
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                background: editingCountry
                  ? alpha("#C4922A", 0.12)
                  : alpha("#2563EB", 0.1),
                color: editingCountry ? "#C4922A" : "#2563EB",
                border: `1px solid ${
                  editingCountry
                    ? alpha("#C4922A", 0.25)
                    : alpha("#2563EB", 0.2)
                }`,
              }}
            />
          </Box>

          {/* ── Form Card ── */}
          <SectionCard delay={50}>
            <CardLabel>
              {editingCountry ? "Edit Country" : "Add New Country"}
            </CardLabel>

            <Box sx={{ display: "flex", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
              {/* Name */}
              <Box sx={{ flex: 1, minWidth: 180 }}>
                <FieldLabel>Country Name *</FieldLabel>
                <Box sx={{ position: "relative" }}>
                  <PublicIcon
                    sx={{
                      position: "absolute",
                      left: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: 15,
                      color: "text.disabled",
                      pointerEvents: "none",
                    }}
                  />
                  <StyledInput
                    placeholder="e.g. Nigeria, Ghana…"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    style={{ paddingLeft: 36 }}
                  />
                </Box>
              </Box>

              {/* Currency */}
              <Box sx={{ flex: 1, minWidth: 160 }}>
                <FieldLabel>Currency *</FieldLabel>
                <Box sx={{ position: "relative" }}>
                  <AttachMoneyIcon
                    sx={{
                      position: "absolute",
                      left: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: 15,
                      color: "text.disabled",
                      pointerEvents: "none",
                    }}
                  />
                  <StyledInput
                    placeholder="e.g. NGN, GHS, USD…"
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    style={{ paddingLeft: 36 }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Flag upload */}
            <Box sx={{ mb: 3 }}>
              <FieldLabel>Country Flag</FieldLabel>
              <FlagUploadZone
                hasfile={displayPreview ? 1 : 0}
                dragover={dragOver ? 1 : 0}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() =>
                  document.getElementById("flag-file-input").click()
                }
              >
                <input
                  id="flag-file-input"
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
                {displayPreview ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.8,
                    }}
                  >
                    <Box
                      component="img"
                      src={displayPreview}
                      alt="Flag preview"
                      sx={{
                        height: 56,
                        maxWidth: 100,
                        borderRadius: 2,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                        objectFit: "cover",
                        mb: 0.5,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#059669",
                        fontFamily: "'Sora', sans-serif",
                      }}
                    >
                      Flag selected
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "text.disabled",
                        fontFamily: "'Sora', sans-serif",
                      }}
                    >
                      Click to change
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Box
                      className="flag-upload-icon"
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 3,
                        background: alpha("#2563EB", 0.08),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 0.6,
                        transition: "transform 0.25s ease",
                      }}
                    >
                      <ImageIcon
                        sx={{ fontSize: 22, color: "#2563EB", opacity: 0.7 }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "text.primary",
                        fontFamily: "'Sora', sans-serif",
                      }}
                    >
                      Upload flag image
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "text.disabled",
                        fontFamily: "'Sora', sans-serif",
                      }}
                    >
                      Drag & drop or click · PNG, JPG · max 5 MB
                    </Typography>
                  </Box>
                )}
              </FlagUploadZone>
            </Box>

            {/* Buttons */}
            <Stack direction="row" spacing={1.5}>
              <PrimaryBtn
                startIcon={
                  editingCountry ? (
                    <CheckIcon sx={{ fontSize: "16px !important" }} />
                  ) : (
                    <AddIcon sx={{ fontSize: "16px !important" }} />
                  )
                }
                disabled={loading || !name.trim()}
                onClick={
                  editingCountry ? handleUpdateCountry : handleCreateCountry
                }
              >
                {loading
                  ? "Saving…"
                  : editingCountry
                  ? "Update Country"
                  : "Add Country"}
              </PrimaryBtn>
              {editingCountry && (
                <Button
                  onClick={resetForm}
                  sx={{
                    borderRadius: 2.5,
                    fontSize: 14,
                    fontWeight: 600,
                    textTransform: "none",
                    border: `1px solid ${alpha("#000", 0.12)}`,
                    color: "text.secondary",
                    px: 2.5,
                    fontFamily: "'Sora', sans-serif",
                    "&:hover": { background: alpha("#000", 0.04) },
                  }}
                >
                  Cancel
                </Button>
              )}
            </Stack>
          </SectionCard>

          {/* ── Countries List Card ── */}
          <SectionCard delay={120}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2.5,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <CardLabel sx={{ mb: 0 }}>All Countries</CardLabel>
              {!fetching && (
                <Chip
                  label={`${countries.length} countr${
                    countries.length !== 1 ? "ies" : "y"
                  }`}
                  size="small"
                  sx={{
                    fontFamily: "'Sora', sans-serif",
                    fontWeight: 700,
                    fontSize: 11,
                    background: alpha("#2563EB", 0.08),
                    color: "#2563EB",
                    border: `1px solid ${alpha("#2563EB", 0.18)}`,
                  }}
                />
              )}
            </Box>

            {/* Skeleton */}
            {fetching ? (
              <Box>
                {[...Array(4)].map((_, i) => (
                  <SkeletonRow key={i} sx={{ animationDelay: `${i * 90}ms` }}>
                    <SkeletonBlock w={44} h={44} radius={11} />
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                      }}
                    >
                      <SkeletonBlock w="40%" h={14} />
                      <SkeletonBlock w="25%" h={11} />
                    </Box>
                    <SkeletonBlock w={60} h={30} radius={8} />
                  </SkeletonRow>
                ))}
              </Box>
            ) : countries.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 5 }}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 3,
                    background: alpha("#2563EB", 0.07),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 1.5,
                  }}
                >
                  <PublicIcon
                    sx={{ fontSize: 26, color: "primary.main", opacity: 0.5 }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    fontWeight: 600,
                  }}
                >
                  No countries added yet
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    mt: 0.4,
                  }}
                >
                  Use the form above to add your first country
                </Typography>
              </Box>
            ) : (
              countries.map((country, i) => (
                <CountryRow
                  key={country.id}
                  itemdelay={i * 55}
                  isediting={editingCountry?.id === country.id ? 1 : 0}
                >
                  {/* Flag avatar */}
                  <FlagAvatar>
                    {country.flag ? (
                      <Box
                        component="img"
                        src={country.flag}
                        alt={country.name}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <PublicIcon
                        sx={{ fontSize: 22, color: "#2563EB", opacity: 0.4 }}
                      />
                    )}
                  </FlagAvatar>

                  {/* Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "text.primary",
                        fontFamily: "'DM Serif Display', serif",
                        lineHeight: 1.2,
                      }}
                    >
                      {country.name}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 0.3,
                      }}
                    >
                      <AttachMoneyIcon
                        sx={{ fontSize: 11, color: "text.disabled" }}
                      />
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: "text.disabled",
                          fontFamily: "'Sora', sans-serif",
                        }}
                      >
                        {country.currency || "No currency set"}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Editing badge */}
                  {editingCountry?.id === country.id && (
                    <Chip
                      label="Editing"
                      size="small"
                      sx={{
                        fontFamily: "'Sora', sans-serif",
                        fontWeight: 700,
                        fontSize: 10,
                        background: alpha("#2563EB", 0.1),
                        color: "#2563EB",
                        border: `1px solid ${alpha("#2563EB", 0.2)}`,
                      }}
                    />
                  )}

                  {/* Edit button */}
                  <Tooltip title="Edit country" arrow>
                    <EditBtn onClick={() => startEdit(country)}>
                      <EditIcon sx={{ fontSize: 13, mr: 0.4 }} />
                      Edit
                    </EditBtn>
                  </Tooltip>
                </CountryRow>
              ))
            )}
          </SectionCard>
        </ContentBox>
      </PageWrapper>
    </ThemeProvider>
  );
};

export default AdminCountryPage